import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { copy } from '@/content/copy';
import { money } from '@/lib/utils';
import { imageSrc, imageUrl } from '@/lib/images';
import { isSoldOut, type Product } from '@/features/products/types';
import { useCart } from '@/features/cart/store';

type Props = {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
};

export function ProductDialog({ product, open, onOpenChange, onAdded }: Props) {
  const add = useCart((state) => state.add);
  // Reset per product by remounting: HomePage keys this component on the slug,
  // so opening another product starts from a clean size and cover photo.
  const [size, setSize] = useState('');
  const [activeImage, setActiveImage] = useState(0);

  if (!product) return null;

  const soldOut = isSoldOut(product);
  const needsSize = product.sizes.length > 0 && !size;
  const main = product.images[activeImage] ?? product.images[0];

  const handleAdd = () => {
    if (soldOut || needsSize) return;
    add(product.id, size);
    onOpenChange(false);
    onAdded();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[980px] gap-0 p-0" aria-labelledby="product-name">
        <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr]">
          <div>
            {main ? (
              <img
                src={imageUrl(main.path)}
                alt={main.alt || product.name}
                width={800}
                height={1000}
                className="aspect-[4/5] w-full bg-[#111] object-cover"
                decoding="async"
              />
            ) : (
              <div className="aspect-[4/5] w-full bg-[#111]" />
            )}

            {product.images.length > 1 && (
              <div className="flex gap-2 p-2.5">
                {product.images.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    aria-label={copy.product.showPhoto(index + 1)}
                    aria-pressed={index === activeImage}
                    onClick={() => setActiveImage(index)}
                    className="size-16 overflow-hidden border border-brand-line transition-colors hover:border-gold aria-pressed:border-gold"
                  >
                    <img
                      src={imageSrc(image)}
                      alt=""
                      width={64}
                      height={64}
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3.5 px-9 pt-11 pb-9 max-[860px]:px-[22px] max-[860px]:py-7">
            <h2 id="product-name" className="font-serif text-[2.2rem] leading-[1.1]">
              {product.name}
            </h2>
            <p className="font-serif text-[1.7rem] font-semibold text-gold-soft">
              {money(product.price)}
            </p>

            {product.description && (
              <p className="text-[#cfc6ba]">{product.description}</p>
            )}

            {product.sizes.length > 0 && (
              <fieldset className="border-0 p-0">
                <legend className="mb-2 text-[0.85rem] text-brand-muted">{copy.product.size}</legend>
                <RadioGroup value={size} onValueChange={setSize}>
                  {product.sizes.map((value) => (
                    <RadioGroupItem key={value} value={value} id={`size-${value}`}>
                      {value}
                    </RadioGroupItem>
                  ))}
                </RadioGroup>
              </fieldset>
            )}

            <Button
              type="button"
              onClick={handleAdd}
              disabled={soldOut || needsSize}
              className="mt-auto w-full"
            >
              {soldOut
                ? copy.product.soldOut
                : needsSize
                  ? copy.product.selectSize
                  : copy.product.addToCart}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
